import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Search, Download, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const fmtDate = v => v ? v.slice(0, 10) : '—';
const fmtCurrency = v => `$${parseFloat(v || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_BADGE = {
  expired:  'bg-red-100 text-red-700',
  expiring: 'bg-amber-100 text-amber-700',
  active:   'bg-emerald-100 text-emerald-700',
};

export default function FundExpiry() {
  const [rows, setRows]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [days, setDays]     = useState('60');
  const [page, setPage]     = useState(1);
  const limit = 25;

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ search, status, days });
    api.get(`/reports/fund-expiry?${p}`)
      .then(r => setRows(r.data))
      .catch(() => toast.error('Failed to load fund expiry data'))
      .finally(() => setLoading(false));
  }, [search, status, days]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search, status, days]);

  const totalPages = Math.max(1, Math.ceil(rows.length / limit));
  const from = rows.length === 0 ? 0 : (page - 1) * limit + 1;
  const to   = Math.min(page * limit, rows.length);
  const pageData = rows.slice(from > 0 ? from - 1 : 0, to);

  function downloadCSV() {
    if (!rows.length) return;
    const cols = ['Client', 'NDIS Number', 'Fund', 'Start Date', 'Expiry Date', 'Days Until Expiry', 'Fund Amount', 'Spent', 'Remaining', 'Status'];
    const data = rows.map(r => [r.client_name, r.ndis_number || '', r.fund_name, fmtDate(r.start_date), fmtDate(r.expiry_date), r.days_until_expiry, r.fund_amount, r.spent_amount, r.remaining, r.expiry_status]);
    const csv  = [cols, ...data].map(r => r.join(',')).join('\n');
    const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `fund_expiry_${new Date().toISOString().slice(0,10)}.csv`; a.click();
  }

  return (
    <div>
      {/* Controls */}
      <div className="flex items-center gap-3 px-4 py-3 bg-white border-b border-slate-200 flex-wrap">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input type="text" placeholder="Search client..." value={search}
            onChange={e => setSearch(e.target.value)}
            className="border border-slate-300 rounded pl-8 pr-3 py-1.5 text-sm w-48 focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>

        <select value={status} onChange={e => setStatus(e.target.value)}
          className="border border-slate-300 rounded px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="all">All Expiring</option>
          <option value="expired">Expired</option>
          <option value="expiring">Expiring Soon</option>
        </select>

        <select value={days} onChange={e => setDays(e.target.value)}
          className="border border-slate-300 rounded px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="30">Next 30 days</option>
          <option value="60">Next 60 days</option>
          <option value="90">Next 90 days</option>
          <option value="180">Next 6 months</option>
        </select>

        <div className="flex-1" />
        <span className="text-sm text-slate-500">{rows.length} fund{rows.length !== 1 ? 's' : ''}</span>
        <button onClick={downloadCSV} className="p-1.5 border border-slate-300 rounded text-slate-500 hover:bg-slate-50" title="Download CSV">
          <Download size={15} />
        </button>
      </div>

      {/* Table */}
      <div className="bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200">
              {['Client', 'NDIS Number', 'Fund', 'Start Date', 'Expiry Date', 'Days Left', 'Fund Amount', 'Spent', 'Remaining', 'Status'].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-700 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={10} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>
            ) : !pageData.length ? (
              <tr><td colSpan={10} className="px-4 py-10 text-center text-slate-400">No records found</td></tr>
            ) : pageData.map((r, i) => (
              <tr key={r.id || i} className="border-b border-slate-100 hover:bg-slate-50/50">
                <td className="px-4 py-2.5 font-medium whitespace-nowrap">
                  <Link to={`/clients/${r.client_id}`} className="text-blue-600 hover:underline">{r.client_name}</Link>
                </td>
                <td className="px-4 py-2.5 text-slate-500 text-xs">{r.ndis_number || '—'}</td>
                <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap">{r.fund_name || '—'}</td>
                <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap">{fmtDate(r.start_date)}</td>
                <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap font-medium">{fmtDate(r.expiry_date)}</td>
                <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap">
                  {r.days_until_expiry < 0
                    ? <span className="text-red-600 font-medium">{Math.abs(r.days_until_expiry)}d ago</span>
                    : <span className={r.days_until_expiry <= 14 ? 'text-amber-600 font-medium' : ''}>{r.days_until_expiry}d</span>}
                </td>
                <td className="px-4 py-2.5 text-right text-slate-600 whitespace-nowrap">{fmtCurrency(r.fund_amount)}</td>
                <td className="px-4 py-2.5 text-right text-slate-600 whitespace-nowrap">{fmtCurrency(r.spent_amount)}</td>
                <td className="px-4 py-2.5 text-right font-medium whitespace-nowrap text-slate-700">{fmtCurrency(r.remaining)}</td>
                <td className="px-4 py-2.5 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${STATUS_BADGE[r.expiry_status] || ''}`}>
                    {r.expiry_status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-slate-200 text-sm text-slate-600 flex-wrap gap-2">
        <span>Showing {from}–{to} of {rows.length} funds</span>
        <div className="flex items-center gap-1">
          <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page <= 1}
            className="p-1.5 rounded border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40">
            <ChevronLeft size={14} />
          </button>
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            const pg = totalPages <= 5 ? i + 1 : page <= 3 ? i + 1 : page + i - 2;
            if (pg > totalPages) return null;
            return (
              <button key={pg} onClick={() => setPage(pg)}
                className={`w-7 h-7 rounded text-xs font-medium border ${pg === page ? 'bg-blue-600 text-white border-blue-600' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                {pg}
              </button>
            );
          })}
          <button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page >= totalPages}
            className="p-1.5 rounded border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40">
            <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
